import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@reflex/ui/components/ui/button';
import { PageTitle, PageDescription } from '@reflex/ui/components/layout/Typography';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/auth/useAuth';
import { orgSetupApi } from '@/lib/org-setup-api';
import { orgBasePath } from '@/lib/routes';
import { docs } from '../onboardingDocs';

/**
 * Final stage: completing bootstrap is the wizard's only remaining action,
 * so the stage offers exactly one button and lands the user on the org home.
 * Plugin configuration is deliberately not a second destination here — the
 * copy points at Admin → Plugins, which is where an admin returns to it any
 * time after setup.
 */
export function DoneStage({ orgId, orgSlug }: { orgId: string; orgSlug: string }) {
  const navigate = useNavigate();
  const auth = useAuth();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: () => orgSetupApi.bootstrapComplete(orgId),
    onSuccess: async () => {
      toast.success('Organization setup complete');
      await auth.refreshSession();
      await qc.invalidateQueries();
      navigate(`${orgBasePath(orgSlug)}/`, { replace: true });
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to complete setup'),
  });
  return (
    <>
      <PageTitle docs={docs('stage-4-finish')}>You're all set</PageTitle>
      <PageDescription>
        Reflex has set up a starting configuration based on your work type. You're ready to go. To
        review or adjust plugins later, visit <strong>Admin → Plugins</strong>.
      </PageDescription>
      <div className="mt-6 flex items-center justify-end border-t border-border/40 pt-4">
        <Button
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          data-testid="org-setup-done-enter"
        >
          {mutation.isPending ? 'Finishing…' : 'Enter organization'}
        </Button>
      </div>
    </>
  );
}
